import './App.css';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Menu from './pages/Menu';
import Lunch from './pages/Lunch';
import News from './pages/News';
import HaekelTreff from './pages/HaekelTreff';
import KuerbisSchnitzen from './pages/KuerbisSchnitzen';
import About from './pages/About';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import GoogleAnalytics from './components/GoogleAnalytics';
import { siteInfo } from './data/siteInfo';

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNav, setShowNav] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const lastScrollY = useRef(0);
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY.current;
      const nearTop = currentScrollY < 40;

      // Keep the white "scrolled" style for upward/visible behavior, not while hiding on downward scroll.
      setScrolled(!nearTop && (!scrollingDown || menuOpen));
      if (nearTop) {
        setShowNav(true);
      } else if (scrollingDown && currentScrollY > 40 && !menuOpen) {
        setShowNav(false);
      } else if (!scrollingDown) {
        setShowNav(true);
      }

      lastScrollY.current = currentScrollY;
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const onPointerDown = (event) => {
      if (!navRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 960) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleNavLinkClick = () => {
    setMenuOpen(false);
  };

  return (
    <div className='app-shell'>
      <GoogleAnalytics />
      <nav ref={navRef} className={`site-nav ${showNav ? 'is-visible' : 'is-hidden'} ${scrolled ? 'is-scrolled' : ''}`}>
        <div className='site-nav__inner container'>
          <Link to='/' className='site-nav__brand' aria-label="Sarita's - Café • Bistro Startseite">
            <img src='/Sartias_Logo_complete.svg' alt="Sarita's - Café • Bistro" className='site-nav__brand-image' />
          </Link>

          <div className='site-nav__desktop-links'>
            <NavLink to='/' className='site-nav__link' onClick={handleNavLinkClick}>Startseite</NavLink>
            <NavLink to='/menu' className='site-nav__link' onClick={handleNavLinkClick}>Speisekarte</NavLink>
            <NavLink to='/lunch' className='site-nav__link' onClick={handleNavLinkClick}>Mittagessen</NavLink>
            {/* Galerie link hidden for now; restore when the page is reactivated. */}
            <NavLink to='/news' className='site-nav__link' onClick={handleNavLinkClick}>Aktuell</NavLink>
            <NavLink to='/about' className='site-nav__link' onClick={handleNavLinkClick}>Über uns</NavLink>
            <NavLink to='/contact' className='site-nav__link' onClick={handleNavLinkClick}>Kontakt</NavLink>
          </div>

          <button
            type='button'
            className={`site-nav__toggle ${menuOpen ? 'is-open' : ''}`}
            aria-expanded={menuOpen}
            aria-label='Navigationsmenü umschalten'
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <span className='site-nav__toggle-icon' aria-hidden='true'>
              <span className='line-1'></span>
              <span className='line-2'></span>
              <span className='line-3'></span>
              <span className='line-4'></span>
            </span>
          </button>
        </div>

        <div className={`site-nav__panel ${menuOpen ? 'is-open' : ''}`}>
          <div className='site-nav__links'>
            <NavLink to='/' className='site-nav__link' onClick={handleNavLinkClick}>Startseite</NavLink>
            <NavLink to='/menu' className='site-nav__link' onClick={handleNavLinkClick}>Speisekarte</NavLink>
            <NavLink to='/lunch' className='site-nav__link' onClick={handleNavLinkClick}>Mittagessen</NavLink>
            {/* Galerie link hidden for now; restore when the page is reactivated. */}
            <NavLink to='/news' className='site-nav__link' onClick={handleNavLinkClick}>Aktuell</NavLink>
            <NavLink to='/about' className='site-nav__link' onClick={handleNavLinkClick}>Über uns</NavLink>
            <NavLink to='/contact' className='site-nav__link' onClick={handleNavLinkClick}>Kontakt</NavLink>
          </div>
        </div>
      </nav>

      <main className='site-main'>
        <Routes>
          <Route path='/' element={<Home />} />
          <Route path='/menu' element={<Menu />} />
          <Route path='/lunch' element={<Lunch />} />
          {/* Galerie route hidden for now; restore when the page is reactivated. */}
          <Route path='/news' element={<News />} />
          <Route path='/haekel-treff' element={<HaekelTreff />} />
          <Route path='/kuerbis-schnitzen' element={<KuerbisSchnitzen />} />
          <Route path='/about' element={<About />} />
          <Route path='/contact' element={<Contact />} />
          <Route path='/datenschutz' element={<Privacy />} />
        </Routes>
      </main>

      <footer className='site-footer'>
        <div className='site-footer__inner'>
          <div className='site-footer__column'>
            <p className='site-footer__title'>Öffnungszeiten</p>
            {siteInfo.openingHours.map((entry) => (
              <p key={entry.days} className='site-footer__text'>{entry.days}: {entry.hours}</p>
            ))}
          </div>
          <div className='site-footer__column'>
            <p className='site-footer__title'>Standort</p>
            <p className='site-footer__text'>{siteInfo.addressLine1}</p>
            <p className='site-footer__text'>{siteInfo.addressLine2}</p>
          </div>
          <div className='site-footer__column'>
            <p className='site-footer__title'>Kontakt</p>
            <p className='site-footer__text'>Tel. <a href={siteInfo.phoneHref}>{siteInfo.phone}</a></p>
            <p className='site-footer__text'>
              E-Mail: <a href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a>
            </p>
            <p className='site-footer__text'>
              Instagram: <a href={siteInfo.instagramUrl} target='_blank' rel='noreferrer'>{siteInfo.instagramHandle}</a>
            </p>
          </div>
          <div className='site-footer__legal'>
            <Link to='/datenschutz'>Datenschutzerklärung</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
